import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Justify,
  Row,
  Col,
  TextArea,
  Modal,
  Button,
  notification,
  StatusTip,
} from 'tea-component';
import NewsBox from '../NewsBox';
import TrendsLabel from '../TrendsLabel';
import TagSelecter from '../TagSelecter';

import SvgIcon from '@/pages/home/components/SvgIcon';

import DefaultImg from '@/pages/opinion/assets/imgs/trends-default-img.jpg';
import {
  commentMap,
  newsTitleMap,
  relatedNewsTitle,
  relatedTwitterTitle,
  tagMap,
  titleMap,
  topImgMap,
  userTagMap,
} from '../../constants';
import { trendsReviewsFormat } from '@/pages/opinion/utils/value';
import './index.scss';
import { getLanguageText } from '../../utils';
import RenderImg from '@/pages/opinion/components/RenderImg';
import { openWindowUrl } from '@/pages/opinion/utils/url';
import { postTrendsUserComment } from '@/pages/opinion/services/trends';
import { useIntl } from 'umi';
import NoDataCommon from '@/pages/opinion/components/NoDataCommon';
import { NO_DATE } from '@/pages/opinion/constants';
import { dealwithImgUrl } from '@/pages/opinion/utils/cdnUrl';
import DOMPurify from 'dompurify';

// 是否放弃编辑弹窗
const TrendsDismissPop = (props: any) => {
  const { visible, onClose, onConfirm } = props;
  const { formatMessage } = useIntl();
  return (
    <Modal
      className="trends-item-popbox"
      visible={visible}
      onClose={() => {
        onClose();
      }}
    >
      <Modal.Body className="trends-modal">
        <div className="trends-modal-title">
          {formatMessage({ id: "Don't save the edited content?" })}
        </div>
        <div className="trends-modal-text">
          {formatMessage({
            id: 'You haven’t saved this content. It will be lost if you confirm.',
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            onConfirm();
            onClose();
          }}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button
          type="weak"
          onClick={() => {
            onClose();
          }}
        >
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default function TrendsItem({
  data,
  editable,
  trendsType = 'google',
  language,
  beginEditNote,
  closeEditNote,
  reload,
  canUpdate,
}: any) {
  // editable是否支持编辑
  // trendsType 在twitter下不展示related queries
  let {
    id,
    comment_user,
    hot_index_number,
    news_img,
    rank,
    related_news,
    related_queries,
    trends_url,
    tag_is_set,
    news_media,
  } = data || {};

  const { formatMessage } = useIntl();
  const getLanguageTextFn = getLanguageText(data, language);
  const tagList = useMemo(() => {
    let list = tag_is_set
      ? getLanguageTextFn(userTagMap)
      : getLanguageTextFn(tagMap);

    if (Array.isArray(list)) {
      return list.filter(Boolean);
    }

    return [list].filter(Boolean);
  }, [data]);

  let newsImg = dealwithImgUrl(news_img);
  let newsTitleList = getLanguageTextFn(newsTitleMap, true);
  const [tags, setTags] = useState<Array<string>>([]);
  // 获取笔记
  const hasCitation = comment_user.trim() !== '';

  const [showMore, setShowMore] = useState(false);

  const [citationText, setCitationText] = useState('');

  const restCitationText = () => {
    let noteText = getLanguageTextFn(commentMap);
    setCitationText(noteText);
  };

  useEffect(() => {
    restCitationText();
  }, [data]);

  const [popVisible, setPopVisible] = useState(false);

  // 默认显示3条，trendsType 在twitter下最多显示10条news
  let maxNewsCount = 3;
  if (trendsType == 'twitter') {
    maxNewsCount = 10;
  }

  // 这里获取Related Queries内容
  const relatedQueriesElement = useMemo(() => {
    if (trendsType === 'twitter') {
      return null;
    }
    let relatedQueries: Array<{ title: string; trends_url: string }> = [];
    try {
      relatedQueries = JSON.parse(related_queries || '[]');
    } catch (error) {
      console.log(error);
    }

    return (
      <div className="trends-item-content-boxwrap">
        <div className="trends-item-content-titbox">
          {formatMessage({ id: 'Related Queries' })}
        </div>
        <div className="trends-item-content-querybox">
          {relatedQueries?.length > 0 ? (
            relatedQueries.map((item: any) => {
              const languageText = getLanguageText(item, language);
              return (
                <TrendsLabel
                  interactable={true}
                  primary={true}
                  key={item.trends_url}
                  text={languageText(titleMap)}
                  onClick={() => openWindowUrl(item.trends_url)}
                />
              );
            })
          ) : (
            <NoDataCommon
              emptyTitleText={NO_DATE.SIMPLE}
              hasDesc={false}
              hasTitle
              style={{ height: 16, margin: '10px 0' }}
              hasImg={false}
            ></NoDataCommon>
          )}
        </div>
      </div>
    );
  }, [related_queries, trendsType, language, formatMessage]);

  function dealwithPlatformInfo(
    item: any,
    platform: string,
  ): {
    newsTitle: string;
    newsImg: string;
    newsMedia: string;
    newsUrl: string;
  } {
    const keyMap: any = {
      google: {
        newsTitle: relatedNewsTitle,
        newsImg: 'news_img',
        newsMedia: 'news_media',
        newsUrl: 'news_url',
      },
      twitter: {
        newsTitle: relatedTwitterTitle,
        newsImg: 'tweets_img',
        newsMedia: 'account_name',
        newsUrl: 'tweets_url',
      },
    };
    const languageText = getLanguageText(item, language);
    const map = keyMap[platform];
    const result: any = {};
    for (const key in map) {
      let element = map[key];
      const value =
        typeof element === 'string' ? item[element] : languageText(element);
      result[key] = value;
    }
    return result;
  }

  // 获取cardBody的内容，展开与不展开会有所区别
  const relatedNewsElement = useMemo(() => {
    // 展开显示内容
    let newsList: Array<any> = [];
    try {
      newsList = JSON.parse(related_news);
    } catch (error) {
      console.log(error);
    }
    return (
      <div className="trends-item-content-boxwrap">
        <div className="trends-item-content-titbox">
          {formatMessage({
            id: `Related ${trendsType === 'twitter' ? 'Twitter' : 'News'}`,
          })}
        </div>
        {newsList?.length > 0 ? (
          <Row className="">
            {/* trendsType 在twitter下最多显示10条news */}
            {newsList?.slice(0, maxNewsCount).map((item: any, index: any) => {
              let { newsTitle, newsImg, newsMedia, newsUrl } =
                dealwithPlatformInfo(item, trendsType);
              newsImg = dealwithImgUrl(newsImg);
              return (
                <Col span={8} key={newsTitle}>
                  <NewsBox
                    key={index}
                    onClick={() => openWindowUrl(newsUrl)}
                    newsImg={newsImg}
                    newsTitle={newsTitle}
                    newsMedia={newsMedia}
                  />
                </Col>
              );
            })}
          </Row>
        ) : (
          <div className="trends-item-content-text">
            {formatMessage({ id: 'No Related' })}
          </div>
        )}
      </div>
    );
  }, [related_news, trends_url, news_img, language]);

  const [loading, setLoading] = useState(false);
  // 提交编辑内容
  const confirmSubmit = () => {
    setLoading(true);
    postTrendsUserComment({
      id,
      origin: trendsType,
      tag_user: tags,
      comment_user: citationText,
    })
      .then((data: number) => {
        closeEditNote();
        if (data === 0) {
          notification.success({
            description: formatMessage({ id: 'Edit Success' }) + '！',
          });
          reload();
        } else {
          notification.error({
            description: formatMessage({ id: 'Edit Fail' }) + '！',
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="dbo-trends-item" id={id}>
      <div
        className="dbo-trends-item-hd"
        onClick={() => {
          setShowMore(!showMore);
        }}
      >
        <Justify
          left={
            <div className="trends-item-title">
              {rank <= 3 ? (
                <img className="trends-item-icon-svg" src={topImgMap[rank]} />
              ) : (
                <span className="trends-item-icon">{rank}</span>
              )}

              <span
                className="trends-item-tit"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(getLanguageTextFn(titleMap)),
                }}
              ></span>
              <span className="trends-item-num">
                {trendsReviewsFormat(hot_index_number)} &nbsp;
                {formatMessage({ id: 'Searches' })}
              </span>
            </div>
          }
          right={
            <>
              <div className={`trends-item-more ${showMore ? 'more' : ''}`}>
                <SvgIcon type="arrow-down" />
              </div>
            </>
          }
        />
      </div>
      <div className="dbo-trends-item-bd">
        {!showMore ? (
          <div
            className={`trends-item-content ${
              newsTitleList.length > 1
                ? 'trends-item-content--multilanguage'
                : ''
            }`}
          >
            <div className="trends-item-content-left">
              <RenderImg
                className="trends-item-img"
                defaultImage={DefaultImg}
                coverImage={newsImg}
              />
              <div className="trends-item-desbox">
                {newsTitleList.map(
                  (item: any, index: any) =>
                    item && (
                      <div
                        onClick={() => openWindowUrl(trends_url)}
                        className="trends-item-des"
                        key={item + index}
                      >
                        <React.Fragment>
                          <span
                            className="trends-item-des-tit"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(item),
                            }}
                          ></span>
                          {news_media ? (
                            <span
                              className="trends-item-des-text"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(news_media),
                              }}
                            ></span>
                          ) : null}
                        </React.Fragment>
                      </div>
                    ),
                )}
              </div>
            </div>

            <div className="trends-item-content-right">
              {!hasCitation ? (
                canUpdate && (
                  <div
                    className="trends-item-content-edit"
                    onClick={() => {
                      // 外层点击edit直接展开
                      setShowMore(true);
                      beginEditNote();
                    }}
                  >
                    <SvgIcon type="pencil-m" />
                    <span>{formatMessage({ id: 'Edit' })}</span>
                  </div>
                )
              ) : (
                <div className="trends-item-note">
                  {formatMessage({ id: 'Note' })}: {citationText}
                </div>
              )}
              {tagList?.length > 0 && (
                <div className="trends-item-labelbox">
                  {tagList.map((item: any) => (
                    <TrendsLabel key={item} text={item} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="trends-item-content more">
            {relatedNewsElement}

            {/* trendsType 在twitter下不展示related queries */}
            {relatedQueriesElement}

            <div className="trends-item-content-boxwrap">
              <div className="trends-item-content-box">
                <div className="trends-item-content-titbox">
                  <div className="trends-item-content-tit">
                    {formatMessage({ id: 'Citation' })}
                  </div>
                  {!editable && canUpdate && (
                    <div
                      className="trends-item-content-edit"
                      onClick={() => {
                        beginEditNote();
                      }}
                    >
                      <SvgIcon type="pencil-m" />
                      <span>{formatMessage({ id: 'Edit' })}</span>
                    </div>
                  )}
                  {/* 编辑时更换操作 */}
                  {editable ? (
                    <div className="trends-item-content-edit">
                      <Button
                        disabled={loading}
                        loading={loading}
                        type="link"
                        onClick={() => confirmSubmit()}
                      >
                        {formatMessage({ id: 'Confirm' })}
                      </Button>
                      <Button
                        disabled={loading}
                        type="link"
                        style={{ marginLeft: 10 }}
                        onClick={() => setPopVisible(true)}
                      >
                        {formatMessage({ id: 'Cancel' })}
                      </Button>
                    </div>
                  ) : null}
                </div>
                {/* 编辑时显示输入框 */}
                {!editable ? (
                  <div className="trends-item-content-text">
                    {hasCitation
                      ? citationText
                      : formatMessage({ id: 'No citation yet' })}
                  </div>
                ) : (
                  <TextArea
                    showCount
                    size={'full'}
                    value={citationText}
                    onChange={(value, context) => {
                      setCitationText(value);
                    }}
                    placeholder={formatMessage({
                      id: 'Please Input The Citation',
                    })}
                    onPressEnter={console.log}
                  />
                )}
              </div>
              <div className="trends-item-content-box">
                <div className="trends-item-content-titbox">
                  {formatMessage({ id: 'Tag' })}
                </div>
                {/* 编辑时显示TagSelecter */}
                <div className="trends-item-content-tagbox">
                  {!editable ? (
                    tagList?.length > 0 ? (
                      tagList.map((item: any, index: any) => (
                        <TrendsLabel key={index + item} text={item} />
                      ))
                    ) : (
                      <div className="trends-item-content-text">
                        {formatMessage({ id: 'No Tag' })}
                      </div>
                    )
                  ) : (
                    <TagSelecter
                      selectedValue={tagList}
                      onChange={(value: any) => {
                        setTags(value);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* 编辑弹窗 */}
      {popVisible ? (
        <TrendsDismissPop
          visible={popVisible}
          onClose={() => {
            setPopVisible(false);
          }}
          onConfirm={() => {
            restCitationText();
            closeEditNote();
          }}
        />
      ) : null}
    </div>
  );
}
